import { dateLib } from "@agentcall/core/lib"
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query"
import {
  type DragEvent,
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { File as FileIcon, Link, Loader, Paperclip, Send } from "react-feather"
import { UPLOAD_ACCEPT, uploadFile } from "@/api/client/file"
import type { TicketMessageList200TicketMessagesItem } from "@/api/client/ticket"
import { ticketApi } from "@/api/client/ticket"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input/input"
import { Textarea } from "@/components/ui/textarea/textarea"
import { cn } from "@/lib/utils"

// ファイルタイプ判定ヘルパー
const IMAGE_FILE_EXTENSION_REGEX = /\.(jpe?g|png|gif|webp|bmp|svg)$/i
const isImageFile = (fileType: string | null | undefined, fileName?: string | null) =>
  fileType?.startsWith("image/") || (!!fileName && IMAGE_FILE_EXTENSION_REGEX.test(fileName))

// URLを検出してリンク化する関数
const URL_REGEX = /(https?:\/\/[^\s]+)/g
// Markdown形式 [テキスト](URL) のリンクを検出する正規表現（任意のURL）
const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\(([^)]+)\)/g

// 安全なhrefを生成（プロトコルがない場合はhttps://を付与、危険なプロトコルは除外）
function getSafeHref(url: string): string | null {
  const trimmedUrl = url.trim()
  // 危険なプロトコルをブロック
  if (trimmedUrl.toLowerCase().startsWith("javascript:")) {
    return null
  }
  // プロトコルがない場合はhttps://を付与
  if (!trimmedUrl.match(/^https?:\/\//i)) {
    return `https://${trimmedUrl}`
  }
  return trimmedUrl
}

function linkifyText(text: string, className?: string): React.ReactNode[] {
  const result: React.ReactNode[] = []
  let lastIndex = 0
  let keyIndex = 0

  // まずMarkdown形式のリンクを処理
  const markdownMatches = [...text.matchAll(MARKDOWN_LINK_REGEX)]

  for (const match of markdownMatches) {
    const [fullMatch, linkText, url] = match
    const matchIndex = match?.index ?? 0

    // マッチ前のテキストを処理（通常のURL検出）
    if (matchIndex > lastIndex) {
      const beforeText = text.substring(lastIndex, matchIndex)
      result.push(...linkifyPlainUrls(beforeText, className, keyIndex))
      keyIndex += beforeText.length
    }

    // 安全なhrefを取得
    const safeHref = getSafeHref(url)
    if (safeHref) {
      // Markdownリンクを追加
      result.push(
        <a
          key={`md-${keyIndex++}`}
          href={safeHref}
          target="_blank"
          rel="noopener noreferrer"
          className={cn("underline hover:opacity-80", className)}
        >
          {linkText}
        </a>
      )
    } else {
      // 危険なURLの場合はテキストとして表示
      result.push(<Fragment key={`unsafe-${keyIndex++}`}>{fullMatch}</Fragment>)
    }

    lastIndex = matchIndex + fullMatch.length
  }

  // 残りのテキストを処理
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex)
    result.push(...linkifyPlainUrls(remainingText, className, keyIndex))
  }

  return result.length > 0 ? result : [text]
}

// 通常のURLのみをリンク化する補助関数
function linkifyPlainUrls(text: string, className?: string, startKey = 0): React.ReactNode[] {
  const parts = text.split(URL_REGEX)
  return parts
    .map((part) => {
      if (URL_REGEX.test(part)) {
        URL_REGEX.lastIndex = 0
        return (
          <a
            key={`url-${startKey}-${part}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className={cn("underline hover:opacity-80", className)}
          >
            {part}
          </a>
        )
      }
      return part ? <Fragment key={`text-${startKey}-${part}`}>{part}</Fragment> : null
    })
    .filter(Boolean) as React.ReactNode[]
}

// 改行とURLリンクを処理してレンダリング
function renderMessageContent(message: string, linkClassName?: string) {
  const lines = message.split("\n")
  return lines.map((line, lineIndex) => (
    <Fragment key={line}>
      {lineIndex > 0 && <br />}
      {linkifyText(line, linkClassName)}
    </Fragment>
  ))
}

interface TicketChatProps {
  ticketId: string
  ownerName?: string
}

const MESSAGES_PER_PAGE = 20

export function TicketChat({ ticketId, ownerName = "オーナー" }: TicketChatProps) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [messageText, setMessageText] = useState("")

  // スクロール位置維持用
  const previousScrollHeightRef = useRef<number>(0)
  const shouldScrollToBottomRef = useRef(true)

  // テキスト選択状態を追跡
  const [selection, setSelection] = useState<{ start: number; end: number }>({ start: 0, end: 0 })

  // ファイルプレビュー用のstate
  const [previewFile, setPreviewFile] = useState<{
    file: File
    url: string
    isImage: boolean
  } | null>(null)

  // ファイルアップロード中の状態
  const [isUploading, setIsUploading] = useState(false)
  const [isFileDragging, setIsFileDragging] = useState(false)

  // リンク挿入ダイアログ用のstate
  const [linkDialog, setLinkDialog] = useState<{
    open: boolean
    url: string
    selectionStart: number
    selectionEnd: number
    selectedText: string
  }>({
    open: false,
    url: "",
    selectionStart: 0,
    selectionEnd: 0,
    selectedText: "",
  })

  // useInfiniteQuery でメッセージを取得
  // sort=desc (デフォルト) で新しい順に取得
  // page 1 = 最新メッセージ, page 2 = より古いメッセージ
  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["ticketMessages", ticketId],
    queryFn: async ({ pageParam }) => {
      return ticketApi.ticketMessageList(ticketId, {
        page: String(pageParam),
        limit: String(MESSAGES_PER_PAGE),
        sort: "desc",
        withFiles: "true",
      })
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.ticketMessages.length, 0)
      return loadedCount < lastPage.total ? allPages.length + 1 : undefined
    },
  })

  // 全ページのメッセージを結合して表示順に並べる
  // APIはDESC（新しい順）で返すので:
  // - 各ページ内を逆順にして古い順に
  // - ページ配列を逆順にして古いページを先に
  const messages =
    data?.pages
      .slice()
      .reverse()
      .flatMap((page) => [...page.ticketMessages].reverse()) ?? []

  // 一番下までスクロールする関数
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
    }
  }, [])

  // 古いメッセージを読み込む前にスクロール位置を記録
  const handleLoadMore = useCallback(() => {
    if (isFetchingNextPage || !hasNextPage) return
    previousScrollHeightRef.current = scrollContainerRef.current?.scrollHeight ?? 0
    shouldScrollToBottomRef.current = false
    fetchNextPage()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  // スクロールイベントハンドラ
  const handleScroll = useCallback(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    // スクロール位置が上端に近い場合（20px以内）、さらにメッセージを読み込む
    if (scrollContainer.scrollTop < 20 && hasNextPage && !isFetchingNextPage) {
      handleLoadMore()
    }
  }, [hasNextPage, isFetchingNextPage, handleLoadMore])

  // 初期読み込み後に一番下までスクロール
  useLayoutEffect(() => {
    if (data && shouldScrollToBottomRef.current) {
      scrollToBottom()
    }
  }, [data, scrollToBottom])

  // 古いメッセージ読み込み後にスクロール位置を維持
  useLayoutEffect(() => {
    if (!isFetchingNextPage && previousScrollHeightRef.current > 0) {
      const scrollContainer = scrollContainerRef.current
      if (scrollContainer) {
        const newScrollHeight = scrollContainer.scrollHeight
        scrollContainer.scrollTop = newScrollHeight - previousScrollHeightRef.current
      }
      previousScrollHeightRef.current = 0
    }
  }, [isFetchingNextPage])

  // 既読処理
  const { mutateAsync: markAllAsReadAsync } = ticketApi.useTicketMessageMarkAllAsRead()

  // マウント時に既読処理してからメッセージ再取得
  useEffect(() => {
    markAllAsReadAsync({ ticketId }).then(() => {
      queryClient.invalidateQueries({ queryKey: ["ticketMessages", ticketId] })
    })
  }, [ticketId, markAllAsReadAsync, queryClient])

  // 定期的なデータ更新（最新のメッセージを取得）
  useEffect(() => {
    const interval = setInterval(() => {
      // 新着メッセージの既読処理を待ってからメッセージ取得
      markAllAsReadAsync({ ticketId }).then(() => {
        queryClient.invalidateQueries({ queryKey: ["ticketMessages", ticketId] })
      })
    }, 10000)

    return () => clearInterval(interval)
  }, [ticketId, queryClient, markAllAsReadAsync])

  const createMutation = ticketApi.useTicketMessageCreate({
    mutation: {
      onSuccess: () => {
        setMessageText("")
        shouldScrollToBottomRef.current = true
        // キャッシュを無効化して最新データを取得
        queryClient.invalidateQueries({ queryKey: ["ticketMessages", ticketId] })
      },
    },
  })

  const handleSendMessage = () => {
    if (!messageText.trim()) return
    createMutation.mutate({
      ticketId,
      data: { message: messageText },
    })
  }

  // Shift+Enterで送信
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && event.shiftKey) {
      event.preventDefault()
      handleSendMessage()
    }
  }

  const openFilePreview = (file: File) => {
    const isImage = file.type.startsWith("image/")
    const url = isImage ? URL.createObjectURL(file) : ""
    setPreviewFile({ file, url, isImage })
  }

  // ファイル選択時にプレビューダイアログを表示
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      openFilePreview(file)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleFileDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsFileDragging(false)
    const file = event.dataTransfer.files?.[0]
    if (file) {
      openFilePreview(file)
    }
  }

  const handleFileDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (event.dataTransfer.types.includes("Files")) {
      setIsFileDragging(true)
    }
  }

  const handleFileDragLeave = (event: DragEvent<HTMLDivElement>) => {
    const relatedTarget = event.relatedTarget
    if (relatedTarget instanceof Node && event.currentTarget.contains(relatedTarget)) {
      return
    }
    setIsFileDragging(false)
  }

  // ファイルプレビューダイアログを閉じる
  const handleClosePreview = () => {
    if (previewFile?.url) {
      URL.revokeObjectURL(previewFile.url)
    }
    setPreviewFile(null)
  }

  // ファイルを送信
  const handleSendFile = async () => {
    if (!previewFile) return

    setIsUploading(true)
    try {
      // S3にファイルをアップロード
      const uploadResult = await uploadFile(previewFile.file)

      // メッセージを作成（ファイル添付）
      createMutation.mutate({
        ticketId,
        data: {
          message: previewFile.file.name,
          uploadFile: {
            path: uploadResult.key,
            fileName: uploadResult.fileName,
          },
        },
      })

      handleClosePreview()
    } catch (error) {
      console.error("File upload failed:", error)
      alert("ファイルのアップロードに失敗しました")
    } finally {
      setIsUploading(false)
    }
  }

  const handleAttachClick = () => {
    fileInputRef.current?.click()
  }

  // テキスト選択状態を更新
  const handleSelectionChange = () => {
    const textarea = textareaRef.current
    if (textarea) {
      setSelection({ start: textarea.selectionStart, end: textarea.selectionEnd })
    }
  }

  // リンク挿入ダイアログを開く
  const handleLinkClick = () => {
    const { start, end } = selection
    const selectedText = messageText.substring(start, end)

    setLinkDialog({
      open: true,
      url: "",
      selectionStart: start,
      selectionEnd: end,
      selectedText,
    })
  }

  // リンクを挿入（Markdown形式: [テキスト](URL)）
  const handleInsertLink = () => {
    if (!linkDialog.url.trim()) return

    const { selectionStart, selectionEnd, selectedText, url } = linkDialog
    const linkText = selectedText || url
    const markdownLink = `[${linkText}](${url})`
    const newText =
      messageText.substring(0, selectionStart) + markdownLink + messageText.substring(selectionEnd)

    setMessageText(newText)
    setLinkDialog({ ...linkDialog, open: false, url: "" })
  }

  const formatMessageType = (
    message: TicketMessageList200TicketMessagesItem
  ): "sent" | "received" | "system" | "deleted" => {
    if (message.deletedAt) return "deleted"
    if ((message.senderType as string) === "system") return "system"
    return message.senderType === "owner" ? "sent" : "received"
  }

  return (
    <div className="flex flex-col h-[calc(100svh-270px)] -mx-4">
      <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 px-4 overflow-y-auto">
        <div className="space-y-4 py-4">
          {/* ローディングインジケーター */}
          {(isPending || isFetchingNextPage) && (
            <div className="flex justify-center py-2">
              <Loader className="w-5 h-5 text-slate-400 animate-spin" />
            </div>
          )}
          {!isPending && messages.length === 0 && !isFetchingNextPage && (
            <p className="text-sm text-slate-500 text-center">メッセージがありません</p>
          )}
          {messages.map((message) => {
            const messageType = formatMessageType(message)
            const timestamp = dateLib.formatDateTime(message.createdAt)
            const senderName =
              messageType === "system"
                ? "システム通知"
                : message.senderType === "owner"
                  ? message.owner?.name || ownerName
                  : "OC"

            if (messageType === "deleted") {
              return (
                <div key={message.id} className="flex justify-center">
                  <div className="bg-slate-50 px-4 py-1 rounded-full">
                    <p className="text-xs text-slate-500 font-medium">メッセージを削除しました</p>
                  </div>
                </div>
              )
            }

            if (messageType === "system") {
              return (
                <div key={message.id} className="flex justify-center">
                  <div className="max-w-[320px] rounded-md border border-slate-200 bg-slate-50 p-2">
                    <div className="flex items-center justify-between gap-2.5 mb-2">
                      <span className="text-xs font-semibold text-slate-700">{senderName}</span>
                      <span className="text-xs font-semibold text-slate-400">{timestamp}</span>
                    </div>
                    <p className="text-sm font-medium leading-5 whitespace-pre-wrap break-words text-slate-700">
                      {renderMessageContent(message.message, "text-blue-600")}
                    </p>
                  </div>
                </div>
              )
            }

            return (
              <div
                key={message.id}
                className={cn(
                  "flex gap-2",
                  messageType === "sent" ? "justify-end" : "justify-start"
                )}
              >
                <div
                  className={cn(
                    "max-w-[275px] rounded-md p-2",
                    messageType === "sent" ? "bg-primary" : "bg-slate-100"
                  )}
                >
                  <div className="flex items-center justify-between gap-2.5 mb-2">
                    <span
                      className={cn(
                        "text-xs font-semibold",
                        messageType === "sent" ? "text-white" : "text-slate-900"
                      )}
                    >
                      {senderName}
                    </span>
                    <span
                      className={cn(
                        "text-xs font-semibold",
                        messageType === "sent" ? "text-slate-300" : "text-slate-400"
                      )}
                    >
                      {timestamp}
                    </span>
                  </div>
                  {/* ファイル表示 */}
                  {message.files && message.files.length > 0 && (
                    <div className="mb-2 space-y-2">
                      {message.files.map((file) => (
                        <div key={file.id}>
                          {isImageFile(file.fileType, file.fileName) ? (
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <img
                                src={file.url}
                                alt={file.fileName || "添付画像"}
                                className="max-w-full h-auto max-h-[200px] object-contain rounded-md"
                              />
                            </a>
                          ) : (
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={cn(
                                "flex items-center gap-2 p-2 rounded-md",
                                messageType === "sent"
                                  ? "bg-white/10 hover:bg-white/20"
                                  : "bg-slate-200 hover:bg-slate-300"
                              )}
                            >
                              <FileIcon
                                size={16}
                                className={messageType === "sent" ? "text-white" : "text-slate-600"}
                              />
                              <span
                                className={cn(
                                  "text-xs underline truncate",
                                  messageType === "sent" ? "text-white" : "text-slate-900"
                                )}
                              >
                                {file.fileName || "添付ファイル"}
                              </span>
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <p
                    className={cn(
                      "text-sm font-medium leading-5 whitespace-pre-wrap break-words",
                      messageType === "sent" ? "text-white" : "text-slate-900"
                    )}
                  >
                    {renderMessageContent(
                      message.message,
                      messageType === "sent" ? "text-blue-200" : "text-blue-600"
                    )}
                  </p>
                </div>
              </div>
            )
          })}
          {/* チャット対応時間案内 */}
          <div className="mx-4 mb-2 p-2 border border-[#ff3232] rounded-md bg-white">
            <p className="text-xs font-medium text-[#ff3232] whitespace-pre-wrap text-pretty">
              【チャット対応時間】{"\n"}9:00〜17:00（※日祝・年末年始を除く）{"\n"}
              時間外・休業日のメッセージは翌営業日に確認します。{"\n"}
              緊急の場合は緊急センターへお電話ください。
            </p>
          </div>
        </div>
      </div>

      <div
        className={cn(
          "px-4 py-2.5 border-t bg-white shadow-[0px_-2px_8px_0px_rgba(19,46,90,0.05)] transition-colors",
          isFileDragging && "bg-blue-50 ring-2 ring-inset ring-blue-300"
        )}
        onDrop={handleFileDrop}
        onDragOver={handleFileDragOver}
        onDragLeave={handleFileDragLeave}
      >
        <div className="space-y-2.5">
          {isFileDragging && (
            <div className="rounded-md border border-dashed border-blue-300 bg-white px-3 py-2 text-center text-xs font-semibold text-blue-600">
              添付するファイルをここにドロップ
            </div>
          )}
          <Textarea
            ref={textareaRef}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={handleKeyDown}
            onSelect={handleSelectionChange}
            onFocus={handleSelectionChange}
            onClick={handleSelectionChange}
            placeholder="メッセージを入力（Shift+Enterで送信）"
            className="min-h-[56px] resize-none border-slate-300 rounded-md text-sm placeholder:text-slate-300"
          />
          <div className="flex items-center justify-between">
            <div className="flex gap-1">
              <input
                ref={fileInputRef}
                type="file"
                accept={UPLOAD_ACCEPT}
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                className="w-8 h-8 p-0 border-slate-300 rounded-md"
                onClick={handleAttachClick}
              >
                <Paperclip size={14} className="text-slate-600" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-8 h-8 p-0 border-slate-300 rounded-md"
                onClick={handleLinkClick}
              >
                <Link size={14} className="text-slate-600" />
              </Button>
            </div>
            <Button
              size="sm"
              className="h-8 px-4 gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm"
              onClick={handleSendMessage}
              disabled={createMutation.isPending || !messageText.trim()}
            >
              <Send size={14} />
              送信
            </Button>
          </div>
        </div>
      </div>

      {/* ファイルプレビューダイアログ */}
      <Dialog open={!!previewFile} onOpenChange={(open) => !open && handleClosePreview()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{previewFile?.isImage ? "画像プレビュー" : "ファイル確認"}</DialogTitle>
          </DialogHeader>
          {previewFile && (
            <div className="relative">
              {previewFile.isImage ? (
                <img
                  src={previewFile.url}
                  alt="プレビュー"
                  className="w-full h-auto max-h-[400px] object-contain rounded-md"
                />
              ) : (
                <div className="flex flex-col items-center justify-center p-8 bg-slate-50 rounded-md">
                  <FileIcon size={48} className="text-slate-400 mb-4" />
                  <p className="text-sm text-slate-600 text-center break-all">
                    {previewFile.file.name}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">
                    {(previewFile.file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={handleClosePreview} disabled={isUploading}>
              キャンセル
            </Button>
            <Button onClick={handleSendFile} disabled={isUploading}>
              {isUploading ? (
                <>
                  <Loader size={14} className="animate-spin mr-2" />
                  送信中...
                </>
              ) : (
                "送信"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* リンク挿入ダイアログ */}
      <Dialog
        open={linkDialog.open}
        onOpenChange={(open) => setLinkDialog({ ...linkDialog, open })}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>リンクを挿入</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {linkDialog.selectedText && (
              <div>
                <p className="text-sm text-slate-500">選択中のテキスト</p>
                <p className="text-sm mt-1 p-2 bg-slate-50 rounded">{linkDialog.selectedText}</p>
              </div>
            )}
            <div>
              <label htmlFor="link-url" className="text-sm font-medium">
                URL
              </label>
              <Input
                id="link-url"
                type="url"
                placeholder="https://example.com"
                value={linkDialog.url}
                onChange={(e) => setLinkDialog({ ...linkDialog, url: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    handleInsertLink()
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog({ ...linkDialog, open: false })}>
              キャンセル
            </Button>
            <Button onClick={handleInsertLink} disabled={!linkDialog.url.trim()}>
              挿入
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
